import { useEffect, useState } from 'react';
import { Alert } from "antd";
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Card, CardContent, CardHeader, CardTitle, Tabs, TabsContent, TabsList, TabsTrigger } from '@shared/ui';
import { ApiError } from '@shared/api';
import {
  useUpdateProfileMutation,
  type ManufacturerProfile,
} from '@entities/manufacturer';
import { profileFormSchema, type ProfileFormValues, toUpdateRequest } from '../model/schema';
import { applyApiFieldErrors } from '@shared/lib/forms/apply-field-errors';
import { KycBadge } from '../lib/kyc-badge';
import { BasicTab } from './tabs/basic-tab';
import { CategoriesTab } from './tabs/categories-tab';
import { MediaTab } from './tabs/media-tab';
import { KycTab } from './tabs/kyc-tab';

function toFormValues(p: ManufacturerProfile): ProfileFormValues {
  return {
    companyName: p.companyName ?? '',
    description: p.description ?? '',
    country: p.country,
    categories: p.categories ?? [],
    // Backend returns MediaLink objects; the form-state holds bare UUIDs.
    logo: p.logo?.id ?? null,
    video: p.video?.id ?? null,
    certificates: p.certificates?.map((l) => l.id) ?? [],
    ecoCertificates: p.ecoCertificates?.map((l) => l.id) ?? [],
    ecoProducer: p.ecoProducer ?? false,
    website: p.website ?? '',
    foundedYear: p.foundedYear,
    employeesRange: p.employeesRange,
  };
}

interface ProfileFormProps {
  profile: ManufacturerProfile;
  defaultTab?: string;
}

export function ProfileForm({ profile, defaultTab = 'basic' }: ProfileFormProps) {
  const { t } = useTranslation();
  const updateMutation = useUpdateProfileMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileFormSchema),
    defaultValues: toFormValues(profile),
    mode: 'onBlur',
  });

  useEffect(() => {
    form.reset(toFormValues(profile));
  }, [profile, form]);

  const onSubmit = (values: ProfileFormValues) => {
    setSubmitError(null);
    updateMutation.mutate(toUpdateRequest(values), {
      onSuccess: (savedProfile) => {
        setSavedAt(Date.now());
        // Clear the dirty flag against the server's authoritative state if
        // it's complete; otherwise fall back to the values we just submitted
        // so uploaded media stays visible in the form.
        form.reset(savedProfile ? toFormValues(savedProfile) : values);
      },
      onError: (err) => {
        // Taxonomy v2: backend rejects PRODUCT-kind categories. Surface the
        // specific message under the Categories tab so the user knows where to
        // look. The picker prevents this at source — this branch handles bugs
        // and stale clients gracefully.
        if (
          err instanceof ApiError &&
          err.code === 'VALIDATION_FAILED' &&
          /not a PRODUCER\/ECO_PRODUCER kind/i.test(err.message)
        ) {
          form.setError('categories', { type: 'server', message: err.message });
          setSubmitError(err.message);
          return;
        }
        if (!applyApiFieldErrors(err, form.setError)) {
          setSubmitError(err instanceof ApiError ? err.message : 'Save failed');
        }
      },
    });
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex flex-col gap-6">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
            <div>
              <CardTitle>{profile.companyName || 'Company profile'}</CardTitle>
              <p className="text-sm text-muted-foreground">{profile.country ?? '—'}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <KycBadge status={profile.kycStatus} />
              {profile.ecoProducer ? <span className="text-xs text-emerald-700">Eco producer</span> : null}
            </div>
          </CardHeader>
        </Card>

        <Tabs defaultValue={defaultTab}>
          <TabsList className="grid w-full grid-cols-4 sm:w-auto">
            <TabsTrigger value="basic">{t('profile.tabs.basic')}</TabsTrigger>
            <TabsTrigger value="categories">{t('profile.tabs.categories')}</TabsTrigger>
            <TabsTrigger value="media">{t('profile.tabs.media')}</TabsTrigger>
            <TabsTrigger value="kyc">{t('profile.tabs.kyc')}</TabsTrigger>
          </TabsList>

          <TabsContent value="basic">
            <Card>
              <CardContent className="pt-6">
                <BasicTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="categories">
            <Card>
              <CardContent className="pt-6">
                <CategoriesTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="media">
            <Card>
              <CardContent className="pt-6">
                <MediaTab />
              </CardContent>
            </Card>
          </TabsContent>
          <TabsContent value="kyc">
            <KycTab profile={profile} isFormDirty={form.formState.isDirty} />
          </TabsContent>
        </Tabs>

        {submitError ? (
          <Alert type="error" showIcon message={submitError} />
        ) : null}

        <div
          style={{
            position: 'sticky',
            bottom: 0,
            background: 'var(--ant-color-bg-elevated)',
            border: '1px solid var(--ant-color-border-secondary)',
            borderRadius: 12,
            padding: 12,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            boxShadow: '0 -4px 16px rgba(0,0,0,0.06)',
            zIndex: 10,
          }}
        >
          <div className="text-xs opacity-70">
            {form.formState.isDirty
              ? t('common.unsavedChanges')
              : savedAt
                ? t('common.savedAt', { time: new Date(savedAt).toLocaleTimeString() })
                : t('common.allChangesSaved')}
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={!form.formState.isDirty || updateMutation.isPending}
              onClick={() => form.reset(toFormValues(profile))}
            >
              {t('common.discard')}
            </Button>
            <Button type="submit" loading={updateMutation.isPending} disabled={!form.formState.isDirty}>
              {t('common.saveProfile')}
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
